import { Box, FormControl, FormHelperText, Stack, Typography } from "@mui/material";
import { ErrorIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { QuestionnaireQuestionDto } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useFormContext } from "react-hook-form";
import { getTimeDurationFormatted } from "../_Shared/helpers";
import { QuestionnaireAnswer, railBorderColor } from "./QuestionnaireAnswer";
import { QuestionnaireGoalCard } from "./QuestionnaireGoalCard";
import type { InvestmentPlan, QuestionnaireQuestionItemDetail } from "./types";
import type { RuleState } from "./useRuleEngine";

type Props = {
  question: QuestionnaireQuestionDto;
  investmentPlans?: InvestmentPlan[];
  externalId: string | null;
  relatedQuestions?: QuestionnaireQuestionDto[];
  ruleState: RuleState;
  /** Continuous position in the questionnaire; sub-questions nested under a plan or a parent get none. */
  order?: number;
  /** Nested under a parent question, which already draws the rail - its answers must not draw a second one. */
  isNested?: boolean;
  isInGoalCard?: boolean;
};

const QuestionNumber = ({ order }: { order?: number }) =>
  order === undefined ? null : (
    <Typography
      variant="overline"
      fontWeight={600}
      color="primary.main"
      letterSpacing=".08em"
      display="block"
      mb={Spacing.S}
      sx={{ breakAfter: "avoid" }}
    >
      {order.toString().padStart(2, "0")}
    </Typography>
  );

const QuestionError = ({ message }: { message?: string }) => (
  <FormHelperText
    component="div"
    sx={{ display: "flex", alignItems: "center", gap: Spacing.S, mr: 0, ml: Spacing.XL, mt: Spacing.M, fontWeight: 500 }}
  >
    <ErrorIcon sx={{ fontSize: 16 }} />
    {message}
  </FormHelperText>
);

const QuestionGroupTitle = ({ title }: { title: string }) => (
  <Typography variant="body1" fontWeight={500} lineHeight={1.4} color="text.primary" sx={{ breakAfter: "avoid" }}>
    {title}
  </Typography>
);

export const QuestionnaireQuestion = ({
  question,
  investmentPlans = [],
  externalId,
  relatedQuestions,
  ruleState,
  order,
  isNested,
  isInGoalCard,
}: Props) => {
  const {
    formState: { errors },
  } = useFormContext();
  const { t } = useResource();

  const questionItems: QuestionnaireQuestionItemDetail[] = question.questionnaireQuestionItems
    .filter((item) => item.parentQuestionItemID === null)
    .map((item) => ({ ...item, isDisabled: ruleState.isItemDisabled(item.questionnaireQuestionItemID) }));

  if (question.subType === "InvestmentPlan") {
    // Not kept on one page as a whole: with several goal cards it can outgrow a page, and Chrome then breaks an
    // over-tall "avoid" box wherever it runs out of space, even mid-answer. The cards keep the rule instead, so the
    // question can only break between them.
    return (
      <Stack spacing={Spacing.L}>
        <Box>
          <QuestionNumber order={order} />
          <QuestionGroupTitle title={question.name} />
        </Box>

        <Stack spacing={Spacing.XL}>
          {investmentPlans?.map((plan) => {
            const isVisible = ruleState.isQuestionVisible(question.questionnaireQuestionID, plan.nsureInvestmentPlanExternalId);
            if (!isVisible) return null;
            return (
              <QuestionnaireGoalCard
                key={plan.nsureInvestmentPlanExternalId}
                title={t(Resources.Questionnaire.Horizon[plan.horizon])}
                details={[{ label: t(Resources.Questionnaire.GoalCard.Horizon), value: getTimeDurationFormatted(plan.horizonDuration, t) }]}
              >
                <Stack spacing={Spacing.XXL}>
                  {relatedQuestions?.map((q) => (
                    <QuestionnaireQuestion
                      key={`${q.questionnaireQuestionID}_${plan.nsureInvestmentPlanExternalId}`}
                      question={q}
                      externalId={plan.nsureInvestmentPlanExternalId}
                      ruleState={ruleState}
                      isInGoalCard
                    />
                  ))}
                </Stack>
              </QuestionnaireGoalCard>
            );
          })}
        </Stack>
      </Stack>
    );
  }

  if (relatedQuestions && relatedQuestions.length > 0) {
    const hasRelatedError = relatedQuestions.some(
      (q) => !!errors[q.questionnaireQuestionID.toString()] || !!errors[`${q.questionnaireQuestionID}___Value`],
    );

    return (
      // Flex gap rather than margins, so the Stack does not reset the rail's own left margin.
      <Stack spacing={Spacing.L} useFlexGap>
        <Box>
          <QuestionNumber order={order} />
          <QuestionGroupTitle title={question.name} />
        </Box>
        <Stack spacing={Spacing.XXL} ml={Spacing.L} pl={Spacing.L} borderLeft={2} borderColor={railBorderColor(hasRelatedError)}>
          {relatedQuestions.map((q) => (
            <QuestionnaireQuestion key={q.questionnaireQuestionID} question={q} ruleState={ruleState} externalId={null} isNested />
          ))}
        </Stack>
      </Stack>
    );
  }

  if (question.subType === "InvestmentOnly") {
    const uniqueFieldName = `${question.questionnaireQuestionID}_${externalId}`;
    const error = errors[uniqueFieldName];

    return (
      <Box sx={{ width: "100%", minWidth: 0, breakInside: "avoid" }}>
        <FormControl component="fieldset" error={!!error} fullWidth data-question-error={!!error}>
          <QuestionnaireAnswer
            type={question.type}
            label={question.name}
            isRequired={question.isRequired}
            fieldName={uniqueFieldName}
            options={questionItems}
            tooltip={question.description ?? undefined}
            isNested={isNested}
            isSubQuestion={isInGoalCard}
          />

          {!!error && <QuestionError message={error.message?.toString()} />}
        </FormControl>
      </Box>
    );
  }

  const fieldName = externalId ? `${question.questionnaireQuestionID}_${externalId}` : question.questionnaireQuestionID.toString();

  const isVisible = ruleState.isQuestionVisible(question.questionnaireQuestionID, externalId ?? null);
  const isOptional = ruleState.isQuestionAllItemsDisabled(questionItems.map((item) => item.questionnaireQuestionItemID));
  if (!isVisible) return null;

  const error = errors[fieldName];

  return (
    <Box sx={{ breakInside: "avoid" }}>
      <QuestionNumber order={order} />
      <FormControl component="fieldset" error={!!error} fullWidth data-question-error={!!error}>
        <QuestionnaireAnswer
          type={question.type}
          fieldName={fieldName}
          options={questionItems}
          label={question.name}
          isRequired={question.isRequired && !isOptional}
          tooltip={question.description ?? undefined}
          isNested={isNested}
          isSubQuestion={isInGoalCard}
        />

        {!!error && <QuestionError message={error.message?.toString()} />}
      </FormControl>
    </Box>
  );
};
